/**
 * What changed, in words, for the confirm-before-publish step.
 *
 * This is the ONLY thing standing between a PM and a scope change in v1. The real
 * impact preview — how many opportunities and opp × asset judgement pairs a change
 * adds — needs to read DE, and the API Lambda deliberately has no route there, so it
 * is a follow-up task. Until then the guardrail is "read back what you are about to
 * do", which catches a slip but not a misjudgement. Nothing here should imply it
 * catches more than that.
 *
 * Pure functions over the wire shapes, so the diff is unit-testable without rendering.
 */

import type {
  HierarchyNode,
  IndustryNodeMapping,
  SyncDateColumnQuery,
  SyncScopeConfiguration,
} from '../../client/IvtHubClient';

/**
 * Which tab a change was made on.
 *
 * Carried so a tab can show its own unsaved count. The page commits everything as one
 * transaction, so someone on the scope tab can otherwise publish industry edits they
 * cannot see — the per-tab count is what makes those edits visible without leaving the
 * tab.
 */
export type ChangeScope = 'scope' | 'industry';

/** One human-readable change. `widening` drives the warning styling. */
export interface ChangeLine {
  text: string;

  scope: ChangeScope;
  /**
   * True when the change can only ADD opportunities to the sync.
   *
   * Widening is the direction that costs money and cannot be undone: added
   * opportunities enter relevance judging, and each (opportunity, asset) pair is a paid
   * Bedrock call. Narrowing is safe — removed opportunities stop refreshing
   * `last_seen_at` and their judgements stay usable inside the purge window, so a
   * mistaken narrowing can be reverted for free.
   */
  widening: boolean;
}

const nodeKey = (n: HierarchyNode) => `${n.level ?? '?'}=${n.name ?? '?'}`;

function nodeKeys(nodes: readonly HierarchyNode[] | undefined): Set<string> {
  return new Set((nodes ?? []).map(nodeKey));
}

/** One column's conditions in words, e.g. `from start of this year, to start of next year`. */
function describeColumn(q: SyncDateColumnQuery | undefined): string {
  const parts = (q?.tokens ?? []).map((t) => `${t.propertyKey} ${t.operator} ${t.value}`);
  return parts.length === 0 ? '(none)' : parts.join(' and ');
}

/**
 * The changes between two configurations, ordered so widening lines come first.
 *
 * An empty array means nothing changed — the caller should not offer to publish.
 */
export function diffSyncScope(
  prev: SyncScopeConfiguration,
  next: SyncScopeConfiguration,
): ChangeLine[] {
  const lines: ChangeLine[] = [];

  // ── date conditions ──────────────────────────────────────────────────────
  // Every column named by either side, so an added or dropped column shows up.
  const columns = [
    ...new Set([...Object.keys(prev.dateConditions ?? {}), ...Object.keys(next.dateConditions ?? {})]),
  ].sort();
  for (const c of columns) {
    const before = describeColumn(prev.dateConditions?.[c as keyof typeof prev.dateConditions]);
    const after = describeColumn(next.dateConditions?.[c as keyof typeof next.dateConditions]);
    if (before === after) continue;
    lines.push({
      text: `${c}: ${before} \u2192 ${after}`,
      scope: 'scope',
      // Whether a bound moved out or in needs today's date to decide, and guessing
      // "safe" on a knob that spends money is the wrong default.
      widening: true,
    });
  }

  // ── record types ─────────────────────────────────────────────────────────
  // An allowlist, so ADDING a type widens the sync and REMOVING one narrows it — the
  // opposite reading from the tag prefixes below, where the list picks what gets scored.
  const prevTypes = new Set(prev.recordTypes ?? []);
  const nextTypes = new Set(next.recordTypes ?? []);
  for (const t of nextTypes) {
    if (!prevTypes.has(t)) {
      lines.push({
        text: `Added record type ${t} — its opportunities start being synced`,
        scope: 'scope',
        widening: true,
      });
    }
  }
  for (const t of prevTypes) {
    if (!nextTypes.has(t)) {
      lines.push({
        // Narrowing, but not harmless: the mirror is upsert-only, so rows already written
        // for this type stop being refreshed rather than being removed.
        text: `Removed record type ${t} — its opportunities stop being synced, and rows already in the cache go stale rather than being deleted`,
        scope: 'scope',
        widening: false,
      });
    }
  }

  // ── tag prefixes ─────────────────────────────────────────────────────────
  const prevTags = new Set(prev.tagPrefixes ?? []);
  const nextTags = new Set(next.tagPrefixes ?? []);
  for (const t of nextTags) {
    if (!prevTags.has(t)) {
      // Adding a prefix widens score DISPATCH only. It does not change what the mirror
      // writes and does not change the Bedrock judgement volume, so it is not flagged
      // as the expensive kind of widening.
      lines.push({
        text: `Added tag prefix ${t} (affects score dispatch only; the mirror's row count does not change)`,
        scope: 'scope',
        widening: false,
      });
    }
  }
  for (const t of prevTags) {
    if (!nextTags.has(t)) {
      lines.push({
        text: `Removed tag prefix ${t} (affects score dispatch only)`,
        scope: 'scope',
        widening: false,
      });
    }
  }

  // ── industry nodes ───────────────────────────────────────────────────────
  const prevByIndustry = new Map<string, IndustryNodeMapping>(
    (prev.industries ?? []).map((m) => [m.industry ?? '', m]),
  );
  for (const nextMapping of next.industries ?? []) {
    const industry = nextMapping.industry ?? '';
    const before = nodeKeys(prevByIndustry.get(industry)?.nodes);
    const after = nodeKeys(nextMapping.nodes);

    for (const k of after) {
      if (!before.has(k)) {
        // The expensive direction, and the reason this dialog exists.
        lines.push({ text: `${industry}: added node ${k}`, scope: 'industry', widening: true });
      }
    }
    for (const k of before) {
      if (!after.has(k)) {
        lines.push({ text: `${industry}: removed node ${k}`, scope: 'industry', widening: false });
      }
    }
  }

  // Widening first: if the list is long, the lines that cost money must be the ones
  // read, not the ones scrolled past.
  return [...lines.filter((l) => l.widening), ...lines.filter((l) => !l.widening)];
}

/** True when any change admits more opportunities into the sync. */
export function hasWidening(lines: readonly ChangeLine[]): boolean {
  return lines.some((l) => l.widening);
}
